import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchSubscription,
  openPortal,
  startCheckout,
  type Subscription,
} from '../../api/billing';

function dollars(cents: number | null | undefined) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(s: string | null) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString();
  } catch {
    return '—';
  }
}

function Toast({ kind, children }: { kind: 'success' | 'info' | 'error'; children: React.ReactNode }) {
  const palette =
    kind === 'success'
      ? 'bg-[#13205A] text-[#C9CDDB] border-[#D02736]/40'
      : kind === 'error'
        ? 'bg-red-900/30 text-red-200 border-red-500/40'
        : 'bg-[#13205A] text-[#9CA3AF] border-[#D02736]/20';
  return (
    <div className={`rounded-md border px-4 py-3 text-sm ${palette}`} role="status">
      {children}
    </div>
  );
}

function PlanCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-[#D02736]/30 bg-[#13205A]/40 p-6 shadow-lg">
      {children}
    </div>
  );
}

function Button({
  onClick,
  busy,
  children,
  variant = 'primary',
}: {
  onClick: () => void;
  busy?: boolean;
  children: React.ReactNode;
  variant?: 'primary' | 'ghost';
}) {
  const base =
    variant === 'primary'
      ? 'bg-[#D02736] text-white hover:bg-[#B81F2E]'
      : 'border border-[#D02736]/40 text-[#C9CDDB] hover:border-[#D02736]';
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className={`rounded-md px-4 py-2 text-sm font-semibold transition disabled:opacity-50 ${base}`}
    >
      {busy ? '…' : children}
    </button>
  );
}

export default function AccountBilling() {
  const [sp, setSp] = useSearchParams();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    const status = sp.get('status');
    if (status === 'success') setInfo('Subscription activated. Welcome aboard.');
    else if (status === 'cancel') setInfo('Checkout was cancelled.');
    if (status) {
      const next = new URLSearchParams(sp);
      next.delete('status');
      setSp(next, { replace: true });
    }
  }, [sp, setSp]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const s = await fetchSubscription();
      setSub(s);
    } catch (e: unknown) {
      setError(extractErr(e, 'Failed to load subscription'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function subscribe() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await startCheckout({});
      window.location.href = url;
    } catch (e: unknown) {
      setError(extractErr(e, 'Failed to start checkout'));
      setBusy(false);
    }
  }

  async function portal() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await openPortal({});
      window.location.href = url;
    } catch (e: unknown) {
      setError(extractErr(e, 'Failed to open billing portal'));
      setBusy(false);
    }
  }

  const graceUntil = useMemo(() => {
    if (!sub || sub.status !== 'past_due' || !sub.currentPeriodEnd) return null;
    return new Date(new Date(sub.currentPeriodEnd).getTime() + 72 * 3600 * 1000);
  }, [sub]);

  return (
    <main className="text-white">
      <section className="max-w-3xl mx-auto px-8 py-10 grid gap-6">
        <h1 className="h-display text-3xl">Account · Billing</h1>
        {info && <Toast kind="success">{info}</Toast>}
        {error && <Toast kind="error">{error}</Toast>}

        {loading ? (
          <div className="text-[#9CA3AF]">Loading…</div>
        ) : !sub ? (
          <div className="text-[#9CA3AF]">No subscription record.</div>
        ) : (
          <>
            {(sub.status === 'none' || sub.status === 'incomplete') && (
              <PlanCard>
                <h2 className="text-xl font-semibold text-white mb-1">
                  Activate your DAL subscription
                </h2>
                {sub.status === 'incomplete' && (
                  <div className="text-sm text-[#C9CDDB] mb-3">
                    Your last checkout was not completed.
                  </div>
                )}
                <p className="text-sm text-[#9CA3AF] mb-4">
                  <span className="text-[#D02736] font-semibold">$500</span> one-time setup
                  fee + <span className="text-white font-semibold">$150</span>/month.
                </p>
                <Button onClick={subscribe} busy={busy}>
                  Subscribe
                </Button>
              </PlanCard>
            )}

            {(sub.status === 'active' || sub.status === 'trialing') && (
              <PlanCard>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-semibold">DAL Subscription</h2>
                  <span className="text-xs uppercase tracking-wider text-[#C9CDDB]">
                    {sub.status}
                  </span>
                </div>
                <dl className="grid grid-cols-2 gap-y-2 text-sm">
                  <dt className="text-[#9CA3AF]">Monthly</dt>
                  <dd>$150.00</dd>
                  <dt className="text-[#9CA3AF]">Next bill date</dt>
                  <dd>{fmtDate(sub.currentPeriodEnd)}</dd>
                  <dt className="text-[#9CA3AF]">Payment method</dt>
                  <dd>
                    {sub.defaultPaymentMethodBrand && sub.defaultPaymentMethodLast4
                      ? `${sub.defaultPaymentMethodBrand} •••• ${sub.defaultPaymentMethodLast4}`
                      : '—'}
                  </dd>
                  <dt className="text-[#9CA3AF]">Setup fee</dt>
                  <dd>{sub.setupPaid ? <span className="text-[#D02736]">Paid ✓</span> : '—'}</dd>
                </dl>
                {sub.cancelAtPeriodEnd && (
                  <div className="mt-4 rounded border border-yellow-500/40 bg-yellow-700/20 p-3 text-sm text-yellow-200">
                    Cancels on {fmtDate(sub.currentPeriodEnd)}. Resume from the billing portal.
                  </div>
                )}
                <div className="mt-5">
                  <Button onClick={portal} busy={busy} variant="ghost">
                    Manage billing
                  </Button>
                </div>
              </PlanCard>
            )}

            {sub.status === 'past_due' && (
              <PlanCard>
                <div className="rounded border border-red-500/40 bg-red-900/20 p-3 text-sm text-red-200 mb-4">
                  Payment failed. Please update your payment method.
                  {graceUntil && (
                    <>
                      {' '}
                      You have access until <strong>{fmtDate(graceUntil.toISOString())}</strong>.
                    </>
                  )}
                </div>
                <Button onClick={portal} busy={busy}>
                  Update payment method
                </Button>
              </PlanCard>
            )}

            {(sub.status === 'canceled' || sub.status === 'unpaid') && (
              <PlanCard>
                <h2 className="text-xl font-semibold mb-2">Your subscription is inactive.</h2>
                <p className="text-sm text-[#9CA3AF] mb-4">
                  Reactivate to continue using DAL.
                </p>
                <Button onClick={subscribe} busy={busy}>
                  Reactivate
                </Button>
              </PlanCard>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function extractErr(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message;
  if (msg) return msg;
  return e instanceof Error ? e.message : fallback;
}
